import { Question, SurveyModel } from "survey-core";
import { createStyles } from "antd-style";
import { Typography } from "antd";
import { LicensePlate } from "@rumbledirect/ui";

interface Feature {
  title: string;
  description: string;
}

const DEFAULT_FEATURES: Feature[] = [
  {
    title: "Landelijk netwerk",
    description: "48 gecertificeerde vestigingen, dealer owned",
  },
  {
    title: "Merk equipment",
    description: "Merk specifiek gereedschap en equipment",
  },
  {
    title: "Merkbeleving",
    description: "Merk eigen vervangend vervoer",
  },
  {
    title: "Schadeherstel via eigen dealer",
    description: "Behoud van restwaarden en fabrieksgarantie",
  },
  {
    title: "Getrainde medewerkers",
    description: "Merk specialistisch getrainde medewerkers",
  },
  {
    title: "Bovag merkgarantie bewijs",
    description: "Audit door importeur",
  },
  {
    title: "Reparatie conform fabrikant",
    description: "Reparatie conform voorschriften fabrikant",
  },
];

export class FeatureListQuestionModel extends Question {
  getType() {
    return "featurelist";
  }

  get heading(): string {
    return this.getPropertyValue("heading") ?? "Goed nieuws!";
  }

  set heading(val: string) {
    this.setPropertyValue("heading", val);
  }

  get subtitle(): string {
    return (
      this.getPropertyValue("subtitle") ?? "Schadeherstel in vertrouwde handen!"
    );
  }

  set subtitle(val: string) {
    this.setPropertyValue("subtitle", val);
  }

  get featuresHeading(): string {
    return (
      this.getPropertyValue("featuresHeading") ??
      "Bij MerkZeker ben je zeker van:"
    );
  }

  set featuresHeading(val: string) {
    this.setPropertyValue("featuresHeading", val);
  }

  get features(): Feature[] {
    return this.getPropertyValue("features") ?? DEFAULT_FEATURES;
  }

  set features(val: Feature[]) {
    this.setPropertyValue("features", val);
  }

  get logoUrl(): string {
    return (
      this.getPropertyValue("logoUrl") ??
      "https://cdn.rumbledirect.com/static/assets/merkzeker/merkzeker-white-bg.png"
    );
  }

  set logoUrl(val: string) {
    this.setPropertyValue("logoUrl", val);
  }
}

const CheckIcon = () => (
  <svg
    style={{ width: 16, height: 16 }}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20 7.5 10.2 17.3 4 11.1"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const useStyles = createStyles(({ css, token }) => ({
  grid: css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 32px;
    margin-bottom: -8px;

    @media (min-width: 768px) {
      grid-template-columns: 1fr 1fr;
      gap: 32px;
    }
  `,
  leftColumn: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  `,
  licensePlateRow: {
    marginTop: 24,
  },
  vehicleText: {
    color: token.colorTextTertiary,
    fontSize: token.fontSizeSM,
    marginTop: 8,
    marginBottom: 24,
  },
  logoContainer: css`
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 200px;
      max-height: 200px;
      width: 100%;
      height: auto;
    }
  `,
  featureList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  checkIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: `2px solid ${token.colorText}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: token.colorText,
    marginTop: 2,
  },
  featureTitle: {
    fontWeight: 700,
    fontSize: token.fontSize,
    color: token.colorText,
    margin: 0,
    lineHeight: 1.4,
  },
  featureDescription: {
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary,
    margin: 0,
    lineHeight: 1.4,
  },
}));

interface FeatureListQuestionProps {
  question: FeatureListQuestionModel;
}

export const FeatureListQuestion = ({ question }: FeatureListQuestionProps) => {
  const { styles } = useStyles();
  const survey = question.survey as SurveyModel;

  const licensePlate = survey.getValue("licensePlate");
  const vehicle = survey.getValue("licensePlate__vehicle") as
    | { brand?: string; model?: string }
    | undefined;

  return (
    <div className={styles.grid}>
      <div className={styles.leftColumn}>
        <Typography.Title level={2} style={{ fontWeight: 900, margin: 0 }}>
          {question.heading}
        </Typography.Title>
        <Typography.Paragraph
          style={{ marginTop: 12, fontWeight: 500, marginBottom: 0 }}
        >
          {question.subtitle}
        </Typography.Paragraph>

        {licensePlate?.identifier && (
          <div className={styles.licensePlateRow}>
            <LicensePlate country={licensePlate.country ?? "NL"}>
              {licensePlate.identifier}
            </LicensePlate>
          </div>
        )}

        {vehicle?.brand && (
          <span className={styles.vehicleText}>
            {vehicle.brand} {vehicle.model}
          </span>
        )}

        <div className={styles.logoContainer}>
          <img src={question.logoUrl} alt="MerkZeker" />
        </div>
      </div>

      <div>
        <Typography.Title level={4} style={{ fontWeight: 700, marginTop: 0 }}>
          {question.featuresHeading}
        </Typography.Title>

        <ul className={styles.featureList}>
          {question.features.map((feature) => (
            <li key={feature.title} className={styles.featureItem}>
              <span className={styles.checkIcon}>
                <CheckIcon />
              </span>
              <div>
                <p className={styles.featureTitle}>{feature.title}</p>
                <p className={styles.featureDescription}>
                  {feature.description}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
